import { GoogleMap, InfoWindow, Marker, MarkerClusterer, OverlayView, Polyline } from "@react-google-maps/api"
import { Select } from "antd"
import { throttle } from "lodash"
import moment from "moment"
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import io from "socket.io-client"
import { mapBus } from "../../assets"
import { CustomModal, Datepicker, TraceEndpoints, VehicleRoute } from "../../Components"
import { secureApi } from "../../Config/api"
import { GET } from "../../utils/apis"
import {
  dateFormate,
  tableDateFormate,
  translations,
  vehicleRouteColors
} from "../../utils/constants"
import { verifyToken } from "../../utils/helpers"
import { useGoogleMaps } from "../../utils/googleMapsLoader"
const { Option } = Select

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 60px)",
}

// Below this zoom vehicles collapse into numbered clusters, so the selected
// route and its stops are hidden to avoid the jumbled overlay.
const ROUTE_DETAIL_MIN_ZOOM = 13
// Stop-name labels only when zoomed close enough that few stops are on
// screen — at street level. Below this, dots only (click for the name).
const STOP_LABEL_MIN_ZOOM = 16

// Mirrors the backend's MAX_OFF_ROUTE_METERS: when the live position drifts
// farther than this from the drawn schedule shape, the route is re-validated.
const OFF_ROUTE_REFETCH_METERS = 600

// Haversine distance in meters (same formula as the backend's stopTimeLatLng).
const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

// Minimum distance from a point to any vertex of the shape — vertex sampling,
// same approach as the backend's minDistanceToShape.
const minDistanceToShapeMeters = (lat, lng, pts) => {
  let min = Infinity
  for (const p of pts) {
    const d = haversineMeters(lat, lng, p.lat, p.lng)
    if (d < min) min = d
  }
  return min
}

const startColors = [
  "#FF0000",
  "#00AA00",
  "#0000FF",
  "#FF9900",
  "#9900FF",
  "#00CCCC",
  "#FF3399",
]
const Maps = ({ language }) => {
  const { isLoaded: isMapScriptLoaded } = useGoogleMaps()
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector((state) => state?.authReducer?.user)
  const organizationDetails = useSelector(
    (state) => state?.authReducer?.organization
  )
  const organizationId = organizationDetails?.id
  // superAdmin picks organizations from the map toolbar filter (multi-select,
  // empty = all); everyone else stays locked to their own organization.
  const [orgFilter, setOrgFilter] = useState([])
  const [organizations, setOrganizations] = useState([])
  // GTFS feed filter (multi-select feed_source values) — admins and superAdmin.
  const [gtfsFilter, setGtfsFilter] = useState([])
  const [gtfsFeeds, setGtfsFeeds] = useState([])
  const effectiveOrgId = user?.role === 'superAdmin' ? orgFilter.join(',') : organizationId
  const gtfsFeedsParam = gtfsFilter.join(',')
  const canFilterGtfs = ['admin', 'superAdmin'].includes(user?.role)
  // feed → organization display name (superAdmin sees feeds across orgs).
  const orgNameById = organizations.reduce((m, o) => ({ ...m, [o.id]: o.organization_name }), {})
  const [locations, setLocations] = useState([])
  const [directionsList, setDirectionsList] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [startPoints, setStartPoints] = useState([])
  const [defaultLocation] = useState({ lat: 45.4642, lng: 9.19 })
  const [isLoading, setIsLoading] = useState(true)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapZoom, setMapZoom] = useState(12)
  const [mapCenter, setMapCenter] = useState(null)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [selectedStartPoint, setSelectedStartPoint] = useState(null)
  const [routeModalOpen, setRouteModalOpen] = useState(false)
  const [routeVehicle, setRouteVehicle] = useState(null)
  const [vehicleStops, setVehicleStops] = useState([])
  const [isStopsLoading, setIsStopsLoading] = useState(false)
  // Schedule-driven panel data for GTFS-attached vehicles (null = GPS view).
  const [scheduleRoute, setScheduleRoute] = useState(null)
  // The panel must keep its skeleton until BOTH fetches land — the stops
  // call returns first and briefly flashed "no route data".
  const [isScheduleLoading, setIsScheduleLoading] = useState(false)
  const [selectedStopInfo, setSelectedStopInfo] = useState(null)
  const [activeVehicleIndex, setActiveVehicleIndex] = useState(null)
  const [isPolylineVisible, setIsPolylineVisible] = useState(false)
  // Filter toggle: render only online vehicles' markers.
  const [showOnlineOnly, setShowOnlineOnly] = useState(false)
  const mapRef = useRef(null)
  const isFirstLoad = useRef(true)
  const userInteracted = useRef(false)
  // Vehicle points waiting for the GoogleMap onLoad — when data arrives before
  // the map script is ready, the initial fit is deferred and applied in onMapLoad.
  const pendingFitPoints = useRef(null)
  // Mirrors of click-selection state, so marker click handlers can read the
  // latest values without markersMemo depending on them — every click was
  // rebuilding the whole MarkerClusterer (visible freeze with many vehicles).
  const activeVehicleIndexRef = useRef(null)
  const isPolylineVisibleRef = useRef(false)
  // The vehicle whose route panel is open — socket ticks live-refresh its
  // stops/ETAs while the panel stays open.
  const routeVehicleRef = useRef(null)
  // When the last selection happened — guards against one physical click
  // firing twice while the selected marker remounts under the cursor.
  const lastSelectRef = useRef({ index: null, at: 0 })
  // Which vehicle's panel was just closed: the next click on that bus turns
  // its route OFF (deselect) instead of re-selecting or reopening the panel.
  const closedPanelIndexRef = useRef(null)
  // Stale-response guard for the schedule fetch: every request bumps the
  // token; a response that lands after a newer request (or after deselect)
  // is dropped instead of painting the previous bus's route.
  const scheduleRequestRef = useRef(0)
  // Same guard for the fleet fetch: applying an org/GTFS filter fires a new
  // request per filter step, and a slower EARLIER (less-filtered) response
  // landing last would replace the filtered fleet with the bigger one.
  const mapDataRequestRef = useRef(0)
  // Current filter signature — a positions response fetched under different
  // filters is dropped (its seed branch could otherwise replant the old fleet).
  const filterSigRef = useRef('')
  filterSigRef.current = `${effectiveOrgId}|${gtfsFeedsParam}`
  // Mirrors of the applied scheduleRoute for use inside socket/interval
  // callbacks — their closures would otherwise capture a stale scheduleRoute.
  const scheduleRouteRef = useRef(null)
  // Token of the (non-silent) request that turned the skeleton on. Silent
  // polls bump scheduleRequestRef too, so without this the loading flag was
  // never cleared when a poll fired while the first request was in flight —
  // the panel then sat on its skeleton forever.
  const scheduleLoadingTokenRef = useRef(0)
  // Consecutive silent polls that came back without a usable schedule. One
  // transient miss must not blank an open panel; only a sustained loss does.
  const scheduleMissStreakRef = useRef(0)
  const SCHEDULE_MISS_TOLERANCE = 3
  const scheduleVehicleRef = useRef(null)

  const isTodaySelected = () => moment(selectedDate).isSame(new Date(), "day")

  useEffect(() => {
    verifyToken(history, dispatch)
  }, [])

  // superAdmin only: load the organization list for the map toolbar filter.
  // Depends on user?.role: on first mount the Redux user often isn't
  // rehydrated yet — a [] effect would bail out and never refetch.
  useEffect(() => {
    if (user?.role !== 'superAdmin') return
    const loadOrganizations = async () => {
      try {
        const res = await secureApi?.get(GET?.GET_ALL_ORGANIZATIONS)
        if (res?.success) setOrganizations(res?.data || [])
      } catch (e) { }
    }
    loadOrganizations()
  }, [user?.role])

  // GTFS feeds for the toolbar filter: superAdmin's list follows the selected
  // organizations (empty = all orgs' feeds); org admins always see their own.
  useEffect(() => {
    if (!canFilterGtfs) return
    const loadGtfsFeeds = async () => {
      try {
        const orgParam =
          user?.role === 'superAdmin' && orgFilter.length
            ? `?organization_id=${orgFilter.join(',')}`
            : ''
        const res = await secureApi?.get(`${GET?.GET_GTFS_FEEDS}${orgParam}`)
        if (res?.success) setGtfsFeeds(res?.data || [])
      } catch (e) { }
    }
    loadGtfsFeeds()
  }, [orgFilter, user?.role])

  useEffect(() => {
    // Full fetch (positions + polylines) only on load / date change. The
    // periodic refresh moves ONLY the live positions — re-downloading every
    // vehicle's polyline (built via per-vehicle OSRM calls server-side) each
    // tick is what made the map hang as data grew.
    const throttledFetch = throttle(fetchPositionsOnly, 10000)
    // Live-refresh the open Vehicle Route panel (current location, next
    // stops, ETAs) while it stays open — no skeleton flash.
    const throttledPanelRefresh = throttle(() => {
      if (routeVehicleRef.current) {
        fetchVehicleStops(routeVehicleRef.current.vehicleId, { silent: true })
      }
    }, 15000)
    // Two-phase load: the light positions call paints the markers in well
    // under a second; the full call (light too — positions + route spans, no
    // polylines) fills in vehicle metadata. Polylines come on demand, one
    // vehicle at a time, when a bus is focused (fetchVehicleRoute).
    if (isTodaySelected()) fetchPositionsOnly()
    fetchDeviceInfo()
    let socket

    if (isTodaySelected()) {
      socket = io()
      socket.on("connect", () => console.log("Socket connected"))
      socket.on("deviceinfo", throttledFetch)
      socket.on("deviceinfo", throttledPanelRefresh)
    }
    return () => {
      if (socket) {
        socket.off("deviceinfo", throttledFetch)
        socket.off("deviceinfo", throttledPanelRefresh)
        socket.disconnect()
      }
    }
  }, [selectedDate, orgFilter, gtfsFilter])

  // The socket refresh above only fires when a device event arrives (and not
  // at all if the socket fails to connect), so the open panel could sit
  // frozen. Poll it on a timer too — silent, so no skeleton flash. Reads the
  // vehicle from the ref: routeVehicle state now also carries live position
  // updates, and depending on it here would reset this timer on every move.
  useEffect(() => {
    if (!routeModalOpen || !isTodaySelected()) return
    const id = setInterval(() => {
      const vehicleId = routeVehicleRef.current?.vehicleId
      if (vehicleId) fetchVehicleStops(vehicleId, { silent: true })
    }, 15000)
    return () => clearInterval(id)
  }, [routeModalOpen, selectedDate])

  // Same for the vehicle markers themselves: without a timer the buses only
  // move when a socket event happens to arrive — with no socket they froze
  // entirely. The positions-only call is light and skips the re-render when
  // nothing actually moved.
  useEffect(() => {
    if (!isTodaySelected()) return
    const id = setInterval(fetchPositionsOnly, 15000)
    return () => clearInterval(id)
  }, [selectedDate, orgFilter, gtfsFilter])

  // On-demand polyline: the initial load skips polylines (fast), so when a
  // vehicle is first focused its route is fetched for it alone.
  const routeFetchedRef = useRef(new Set())

  // The raw GPS trace (snapped=false) is a zig-zag of straight segments
  // between fixes — it used to be drawn at once and replaced by the road
  // line seconds later, which read as a broken map. Hold it: keep polling
  // for the road-snapped line and draw only that. The raw trace goes on the
  // map only as a last resort — snapping failed server-side (snap_failed),
  // or the polls ran out.
  const ROUTE_SNAP_ATTEMPTS = 12
  // Quick first re-polls (the road line usually lands within a second or
  // two of the first answer), then the slower steady cadence.
  const ROUTE_SNAP_POLL_MS = (attempt) => [1000, 1500, 2000][attempt] || 3000
  const fetchVehicleRoute = async (vehicleId, index, attempt = 0) => {
    try {
      const formattedDate = moment(selectedDate).isValid() ? moment(selectedDate).format(tableDateFormate) : null
      const res = await secureApi?.get(
        `${GET?.GET_MAP_HISTORY}?user_id=${user?.id}&organization_id=${effectiveOrgId}&formattedDate=${formattedDate}&vehicle_id=${vehicleId}&gtfs_feeds=${gtfsFeedsParam}`
      )
      // Filters changed meanwhile (the fetched set was reset): this index
      // may now be another bus — drop the answer.
      if (!routeFetchedRef.current.has(vehicleId)) return
      const route = res?.data?.[0]
      if (!res?.success || !route?.polyline?.length) return
      const stillSnapping = route.snapped === false && !route.snap_failed && attempt < ROUTE_SNAP_ATTEMPTS
      if (stillSnapping) {
        setTimeout(() => fetchVehicleRoute(vehicleId, index, attempt + 1), ROUTE_SNAP_POLL_MS(attempt))
        return
      }
      setDirectionsList((prev) => {
        const next = [...prev]
        next[index] = thinPolyline(route.polyline)
        return next
      })
    } catch (e) { }
  }

  useEffect(() => {
    routeFetchedRef.current = new Set()
  }, [selectedDate, orgFilter, gtfsFilter])

  useEffect(() => {
    if (selectedRouteIndex === null) return
    const vehicleId = vehicles[selectedRouteIndex]?.vehicleId
    if (!vehicleId) return
    if (directionsList[selectedRouteIndex]) return
    if (routeFetchedRef.current.has(vehicleId)) return
    routeFetchedRef.current.add(vehicleId)
    fetchVehicleRoute(vehicleId, selectedRouteIndex)
  }, [selectedRouteIndex, directionsList, vehicles])

  async function snapToRoads(points) {
    console.log('points', points)
    const path = points.map(p => `${p.lat},${p.lng}`).join("|");

    const url = `https://roads.googleapis.com/v1/snapToRoads?path=${path}&interpolate=true&key=AIzaSyAOvlaMKD4C0dPSjCtNOi-DnwkW8s4pSbI`;

    const res = await fetch(url);
    const json = await res.json();

    console.log('Snap to Roads response:', json);

    if (!json.snappedPoints) return points;

    return json.snappedPoints.map(p => ({
      lat: p.location.latitude,
      lng: p.location.longitude
    }));
  }

  // Safety cap: never hold/render more than ~4000 points per polyline —
  // beyond that the browser spends all its time diffing markers and paths.
  // (800 turned a road-snapped day into a polygon: a vertex every 40 m.)
  const thinPolyline = (poly, maxPoints = 4000) => {
    if (!Array.isArray(poly) || poly.length <= maxPoints) return poly || []
    const step = Math.ceil(poly.length / maxPoints)
    const thinned = poly.filter((_, i) => i % step === 0)
    if (thinned[thinned.length - 1] !== poly[poly.length - 1]) {
      thinned.push(poly[poly.length - 1])
    }
    return thinned
  }

  const fetchDeviceInfo = async () => {
    const reqToken = ++mapDataRequestRef.current
    try {
      setIsLoading(!isTodaySelected())
      const user_id = user?.id
      const formattedDate = moment(selectedDate).isValid() ? moment(selectedDate).format(tableDateFormate) : null

      // skip_polylines: positions + route endpoints only — the selected
      // vehicle's polyline is fetched on demand (fetchVehicleRoute), so page
      // load never waits on per-vehicle OSRM snapping.
      const res = await secureApi?.get(
        `${GET?.GET_MAP_HISTORY}?user_id=${user_id}&organization_id=${effectiveOrgId}&formattedDate=${formattedDate}&skip_polylines=true&gtfs_feeds=${gtfsFeedsParam}`
      )
      // A newer fetch (another filter step) already superseded this one —
      // applying this older, less-filtered response would resurrect the
      // bigger fleet over the filtered one.
      if (reqToken !== mapDataRequestRef.current) return
      // console.log('res', res)
      if (res?.success && res?.data?.length) {
        const validData = res.data
          ?.filter((i) => i.lat != null && i.lng != null)
          ?.map((i) => ({
            ...i,
            lat: parseFloat(i.lat),
            lng: parseFloat(i.lng),
            polyline: thinPolyline(i.polyline || []),
          }))

        setVehicles(validData)
        setLocations(validData)

        const allDirs = []
        const startDots = []

        validData.forEach((loc, i) => {
          const poly = loc.polyline
          if (poly?.length > 1) {
            allDirs.push(poly)
            startDots.push({
              position: poly[0],
              color: startColors[i % startColors.length],
              vehicleId: loc.vehicleId,
              vehicleName: loc.vehicleName,
              start_time: loc.start_time,
            })
          } else allDirs.push(null)
        })

        setDirectionsList(allDirs)
        // The on-demand route cache is index-keyed — the list was just
        // replaced wholesale, so previously fetched routes are gone and must
        // be allowed to refetch for the selected vehicle.
        routeFetchedRef.current = new Set()
        setStartPoints(startDots)

        if (isFirstLoad.current && validData.length) {
          fitInitialView(validData)
          isFirstLoad.current = false
        }
      } else {
        setDirectionsList([])
        setStartPoints([])
        fallbackToGeolocation()
      }

      setIsLoading(false)
    } catch (err) {
      if (reqToken !== mapDataRequestRef.current) return
      fallbackToGeolocation()
      setIsLoading(false)
    }
  }

  // Initial viewport: fit the map to the area where the fleet actually is.
  // Online devices define the bounds (that's where the live fleet is); if none
  // are online, fall back to all devices. Runs only on first load — afterwards
  // the user's pan/zoom is never overridden.
  const fitInitialView = (data) => {
    const online = data.filter((i) => i.is_online)
    const points = online.length ? online : data
    if (!points.length) return

    const applyFit = (map) => {
      if (points.length === 1) {
        map.panTo({ lat: points[0].lat, lng: points[0].lng })
        map.setZoom(13)
      } else {
        const bounds = new window.google.maps.LatLngBounds()
        points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
        map.fitBounds(bounds, 60)
        // Keep the controlled `center` prop in sync with the fitted view so a
        // later re-render doesn't snap the map back to the pre-fit center.
        const c = bounds.getCenter()
        setMapCenter({ lat: c.lat(), lng: c.lng() })
      }
    }

    if (mapRef.current && window.google?.maps) {
      applyFit(mapRef.current)
    } else {
      // Map script not ready yet — set a sensible center now, fit on onLoad.
      setMapCenter({ lat: points[0].lat, lng: points[0].lng })
      pendingFitPoints.current = points
    }
  }

  // Toolbar fleet filters (org / GTFS feed): switch the whole map — clear any
  // focused vehicle and refit the viewport to the filtered fleet.
  const clearFocusedVehicle = () => {
    setSelectedVehicle(null)
    setSelectedLocation(null)
    setSelectedRouteIndex(null)
    setActiveVehicleIndex(null)
    setIsPolylineVisible(false)
    setScheduleRoute(null)
    setSelectedStopInfo(null)
    setRouteModalOpen(false)
    routeVehicleRef.current = null
    setRouteVehicle(null)
    activeVehicleIndexRef.current = null
    isPolylineVisibleRef.current = false
    closedPanelIndexRef.current = null
    // Invalidate any in-flight schedule request and its mirrors — the fleet
    // (and the selection) is about to change.
    scheduleRequestRef.current += 1
    scheduleRouteRef.current = null
    scheduleVehicleRef.current = null
    // Also invalidate any in-flight FLEET fetch from before the filter
    // change, so its (unfiltered) response can't land after the filtered one.
    mapDataRequestRef.current += 1
    isFirstLoad.current = true
  }

  const handleOrgChange = (values) => {
    setOrgFilter(values || [])
    // The feed list depends on the selected orgs — drop stale feed picks.
    setGtfsFilter([])
    clearFocusedVehicle()
  }

  const handleGtfsChange = (values) => {
    setGtfsFilter(values || [])
    clearFocusedVehicle()
  }

  // Lightweight periodic refresh: update live positions in place, keeping the
  // already-loaded polylines. Returns the same state reference when nothing
  // moved so React (and the MarkerClusterer) skip the re-render entirely.
  const fetchPositionsOnly = async () => {
    try {
      if (!isTodaySelected()) return

      const sig = filterSigRef.current
      const user_id = user?.id
      const formattedDate = moment(selectedDate).isValid() ? moment(selectedDate).format(tableDateFormate) : null

      const res = await secureApi?.get(
        `${GET?.GET_MAP_HISTORY}?user_id=${user_id}&organization_id=${effectiveOrgId}&formattedDate=${formattedDate}&positions_only=true&gtfs_feeds=${gtfsFeedsParam}`
      )

      // Filters changed while this was in flight (stale closure from an old
      // socket/interval) — drop it so the old fleet can't be replanted.
      if (sig !== filterSigRef.current) return

      if (!res?.success || !res?.data?.length) return

      const posMap = new Map(
        res.data
          .filter((p) => p.lat != null && p.lng != null && !Number.isNaN(parseFloat(p.lat)))
          .map((p) => [p.vehicleId, p])
      )

      const merge = (prev) => {
        // First paint: full data hasn't landed yet — seed the markers straight
        // from the light positions payload so the map is usable immediately,
        // while the heavy polyline fetch finishes in the background.
        if (!prev.length) {
          return [...posMap.values()].map((p) => ({
            ...p,
            lat: parseFloat(p.lat),
            lng: parseFloat(p.lng),
            polyline: [],
          }))
        }

        let changed = false

        const next = prev.map((v) => {
          const p = posMap.get(v.vehicleId)
          if (!p) return v

          const lat = parseFloat(p.lat)
          const lng = parseFloat(p.lng)

          if (
            v.lat === lat &&
            v.lng === lng &&
            v.is_online === p.is_online &&
            v.remaining === p.remaining
          ) {
            return v
          }

          changed = true
          return { ...v, lat, lng, is_online: p.is_online, remaining: p.remaining, report_time: p.report_time }
        })

        return changed ? next : prev
      }

      setVehicles(merge)
      setLocations(merge)

      // The identity tooltip and the open panel are click-time snapshots —
      // refresh them from the same feed so their coordinates (and the
      // position's report time) track the live marker.
      setSelectedLocation((prev) => {
        if (!prev) return prev
        const p = posMap.get(prev.vehicleId)
        if (!p) return prev
        const lat = parseFloat(p.lat)
        const lng = parseFloat(p.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return prev
        if (
          prev.lat === lat &&
          prev.lng === lng &&
          prev.remaining === p.remaining &&
          prev.report_time === p.report_time
        ) {
          return prev
        }
        return { ...prev, lat, lng, remaining: p.remaining, is_online: p.is_online, report_time: p.report_time }
      })

      // The open Vehicle Route panel must show the same live coordinates as
      // the moving marker — otherwise its lat/lng only moved when the
      // schedule poll (router logs) happened to land.
      const panelVehicleId = routeVehicleRef.current?.vehicleId
      const panelPos = panelVehicleId ? posMap.get(panelVehicleId) : null
      if (panelPos) {
        const lat = parseFloat(panelPos.lat)
        const lng = parseFloat(panelPos.lng)
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setScheduleRoute((prev) =>
            prev && (prev.bus_position?.lat !== lat || prev.bus_position?.lng !== lng)
              ? { ...prev, bus_position: { lat, lng } }
              : prev
          )
          // The GPS-fallback view reads coordinates/passengers straight from
          // the clicked vehicle object — keep it on the live read too.
          setRouteVehicle((prev) =>
            prev &&
            prev.vehicleId === panelVehicleId &&
            (parseFloat(prev.lat) !== lat ||
              parseFloat(prev.lng) !== lng ||
              prev.remaining !== panelPos.remaining ||
              prev.is_online !== panelPos.is_online ||
              prev.report_time !== panelPos.report_time)
              ? {
                  ...prev,
                  lat,
                  lng,
                  remaining: panelPos.remaining,
                  is_online: panelPos.is_online,
                  report_time: panelPos.report_time,
                }
              : prev
          )
        }
      }

      if (isFirstLoad.current) {
        // First load (or a filter change, which re-arms this flag): fit the
        // viewport to the filtered fleet, not just the first vehicle —
        // otherwise this light call consumes the flag and the full fetch's
        // fitInitialView never runs, leaving the map parked on the old area.
        const pts = res.data
          .filter((d) => d.lat != null && !Number.isNaN(parseFloat(d.lat)))
          .map((d) => ({ ...d, lat: parseFloat(d.lat), lng: parseFloat(d.lng) }))
        if (pts.length) {
          fitInitialView(pts)
          isFirstLoad.current = false
        }
      }

      // Re-validate the drawn schedule shape against the live position: it
      // was gated only when fetched — a bus that has since left its line
      // (run ended, depot run) must not keep drawing the old route. The
      // silent refetch re-matches and, if truly off-route, hides the line.
      const schedVehId = scheduleVehicleRef.current
      const drawnShape = scheduleRouteRef.current?.shape
      if (schedVehId && drawnShape?.length > 1) {
        const sp = posMap.get(schedVehId)
        const sLat = parseFloat(sp?.lat)
        const sLng = parseFloat(sp?.lng)
        if (
          Number.isFinite(sLat) &&
          Number.isFinite(sLng) &&
          minDistanceToShapeMeters(sLat, sLng, drawnShape) > OFF_ROUTE_REFETCH_METERS
        ) {
          fetchVehicleStops(schedVehId, { silent: true })
        }
      }
      setIsLoading(false)
    } catch (err) {
      // periodic refresh only — ignore transient failures
    }
  }

  // silent: refresh the data without flashing the loading skeleton (used by
  // the live socket refresh while the panel is open).
  const fetchVehicleStops = async (vehicleId, { silent = false, fitRoute = false } = {}) => {
    try {
      if (!silent) setIsStopsLoading(true)

      const formattedDate = moment(selectedDate).isValid() ? moment(selectedDate).format(tableDateFormate) : null

      // GTFS-attached vehicles get the schedule-driven panel (official
      // timetable + live early/late offset); others keep the GPS-based view.
      if (!silent) setScheduleRoute(null)
      if (!silent) {
        scheduleRouteRef.current = null
        scheduleVehicleRef.current = null
      }
      if (isTodaySelected()) {
        // Token guards against out-of-order responses: clicking bus A then
        // bus B must never let A's slower response paint A's route for B.
        const reqToken = ++scheduleRequestRef.current
        if (!silent) {
          setIsScheduleLoading(true)
          scheduleLoadingTokenRef.current = reqToken
          scheduleMissStreakRef.current = 0
        }
        secureApi
          .get(`${GET?.GET_VEHICLE_SCHEDULE_ROUTE}?vehicle_id=${vehicleId}`)
          .then((sr) => {
            if (reqToken !== scheduleRequestRef.current) return
            const usable = !!(sr?.success && (sr?.schedule_available || !scheduleRouteRef.current?.schedule_available))
            // "Run finished" / "waiting at the start" are verdicts, not losses:
            // the route comes off the map at once, no tolerance polls.
            const definitive =
              !!sr?.success && !sr?.schedule_available && ['trip_completed', 'awaiting_departure'].includes(sr?.reason)
            if (silent && !usable && !definitive && scheduleRouteRef.current) {
              // Silent refresh lost the schedule (transient error, or the
              // matcher briefly found no credible run): keep showing what we
              // have until the loss persists across several polls.
              scheduleMissStreakRef.current += 1
              if (scheduleMissStreakRef.current < SCHEDULE_MISS_TOLERANCE) return
            }
            scheduleMissStreakRef.current = 0
            // Keep the unavailable response too — the panel shows WHY the
            // schedule didn't match instead of silently falling back.
            setScheduleRoute(sr?.success ? sr : null)
            scheduleRouteRef.current = sr?.success ? sr : null
            scheduleVehicleRef.current = sr?.success ? sr?.vehicle_id || vehicleId : null
            // Auto-fit the map to the whole route when the user just clicked
            // the bus — without this the line arrived at whatever zoom the
            // map happened to be at, often a jumbled city-wide dot cloud.
            // Only on the click itself, never on silent refreshes: those
            // must not yank the map while the user is panning.
            if (fitRoute && sr?.success && sr?.shape?.length > 1 && mapRef.current && window.google?.maps) {
              const bounds = new window.google.maps.LatLngBounds()
              sr.shape.forEach((p) => bounds.extend(p))
              mapRef.current.fitBounds(bounds, 60)
            }
            if (sr && !sr.schedule_available) {
              // Diagnosable in devtools: why this vehicle fell back to the
              // GPS-based view (no feed attached, no GPS today, off-route…).
              console.debug('Schedule route unavailable:', sr?.reason || sr?.message)
            }
          })
          .catch(() => {
            if (reqToken !== scheduleRequestRef.current) return
            // Network hiccup on a silent poll: keep the current panel.
            if (silent && scheduleRouteRef.current) return
            setScheduleRoute(null)
            scheduleRouteRef.current = null
            scheduleVehicleRef.current = null
          })
          .finally(() => {
            // Clear the skeleton when the request that raised it settles —
            // even if a silent poll has since taken over the token.
            if (!silent && reqToken === scheduleLoadingTokenRef.current) setIsScheduleLoading(false)
          })
      } else {
        setScheduleRoute(null)
        scheduleRouteRef.current = null
        scheduleVehicleRef.current = null
      }

      const res = await secureApi.get(`${GET?.GET_VEHICLE_STOPS}?vehicle_id=${vehicleId}&date=${formattedDate}`)
      // console.log(res, 'res stops')
      if (res?.success) {
        setVehicleStops({
          past: res?.past || [],
          current: res?.current || null,
          future: res?.future || []
        })
      } else if (!silent) {
        // A failed silent refresh keeps the last good list on screen.
        setVehicleStops({
          past: [],
          current: null,
          future: []
        })
      }
    } catch (err) {
      if (!silent) {
        setVehicleStops({
          past: [],
          current: null,
          future: []
        })
      }
    } finally {
      if (!silent) setIsStopsLoading(false)
    }
  }

  const fallbackToGeolocation = () => {
    if ("geolocation" in navigator && isFirstLoad.current) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMapCenter({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          })
          isFirstLoad.current = false
        },
        () => {
          setMapCenter(defaultLocation)
          isFirstLoad.current = false
        }
      )
    } else if (isFirstLoad.current) {
      setMapCenter(defaultLocation)
      isFirstLoad.current = false
    }
  }

  const formatStartTime = (t) => moment(t).format("hh:mm A")

  // The router platform's last_time arrives as 'YYYY-MM-DD HH:mm:ss' (or a
  // unix epoch on some firmware); render just the clock, like the platform's
  // own "Report time", so positions can be compared across dashboards.
  const formatReportTime = (t) => {
    if (!t) return null
    const m = /^\d{10}$/.test(String(t))
      ? moment.unix(Number(t))
      : moment(t, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true)
    return m.isValid() ? m.format("HH:mm:ss") : String(t)
  }

  const getDurationAgo = (start, end, isToday = true) => {
    if (!start) return ""
    const s = moment(start)
    const e = end ? moment(end) : moment()
    const dur = moment.duration(e.diff(s))
    const h = Math.floor(dur.asHours())
    const m = dur.minutes()
    return isToday
      ? `Running from ${h} hour${h !== 1 ? "s" : ""} and ${m} minute${m !== 1 ? "s" : ""
      } ago`
      : `This vehicle ran for ${h} hour${h !== 1 ? "s" : ""} and ${m} minute${m !== 1 ? "s" : ""
      }`
  }

  // Integer zoom bucket: icon sizes (and everything memoized on them) only
  // change when the zoom level actually crosses a whole step, not on every
  // fractional tick during a pinch/scroll — that rebuilt all markers.
  const iconZoom = Math.round(mapZoom)

  const getMarkerIcon = useCallback(
    (logoUrl) => {
      if (!mapLoaded || !window.google?.maps) return null
      const size = Math.round(35 * Math.pow(0.95, 12 - iconZoom))
      return {
        url: logoUrl || mapBus,
        scaledSize: new window.google.maps.Size(size, size),
      }
    },
    [mapLoaded, iconZoom]
  )

  const onMapLoad = useCallback((map) => {
    setMapLoaded(true)
    mapRef.current = map
    // Data arrived before the map was ready — apply the deferred initial fit.
    const pts = pendingFitPoints.current
    if (pts?.length && window.google?.maps) {
      pendingFitPoints.current = null
      if (pts.length === 1) {
        map.panTo({ lat: pts[0].lat, lng: pts[0].lng })
        map.setZoom(13)
      } else {
        const bounds = new window.google.maps.LatLngBounds()
        pts.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
        map.fitBounds(bounds, 60)
        const c = bounds.getCenter()
        setMapCenter({ lat: c.lat(), lng: c.lng() })
      }
    }
  }, [])

  const onZoomChanged = useCallback(
    throttle(() => {
      if (mapRef.current) setMapZoom(mapRef.current.getZoom())
    }, 300),
    []
  )

  const getCurrentCenter = () =>
    mapCenter || defaultLocation || { lat: 45.4642, lng: 9.19 }

  const offsetPolylinePath = (path, offsetMeters = 2, direction = 1) => {
    if (!window.google?.maps?.geometry || !Array.isArray(path)) return path

    const offsetPath = []

    for (let i = 0; i < path.length; i++) {
      const p = new window.google.maps.LatLng(path[i].lat, path[i].lng)

      const offPoint = window.google.maps.geometry.spherical.computeOffset(
        p,
        offsetMeters * direction,
        0
      )

      offsetPath.push({
        lat: offPoint.lat(),
        lng: offPoint.lng()
      })
    }

    return offsetPath
  }

  // Booleans instead of the raw zoom keep these memos from rebuilding on
  // every zoom tick — they only care about crossing the thresholds.
  const showRouteDetail = mapZoom >= ROUTE_DETAIL_MIN_ZOOM
  const showStopLabels = mapZoom >= STOP_LABEL_MIN_ZOOM

  // ✅ MEMOIZED POLYLINES
  const polylinesMemo = useMemo(() => {
    // GTFS-attached vehicles draw BOTH lines, same as the route-history map:
    // the schedule's official geometry in BLUE and the bus's actual GPS trace
    // in RED, so drift between schedule and reality is visible. Not gated on
    // zoom: the user explicitly selected this vehicle.
    if (selectedRouteIndex !== null && scheduleRoute?.shape?.length > 1 && window.google?.maps) {
      const shapeSymbol = {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 3,
        strokeColor: "#4285F4",
      }
      const trace = directionsList?.[selectedRouteIndex]
      const shifted = trace?.length > 1 ? offsetPolylinePath(trace, 2) : null
      const traceSymbol = {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 3,
        strokeColor: "#FF0000",
      }

      return (
        <>
          <Polyline
            key="schedule-shape"
            path={scheduleRoute.shape}
            options={{
              strokeColor: "#4285F4",
              strokeWeight: 4,
              strokeOpacity: 0.9,
              icons: [{ icon: shapeSymbol, offset: "0%", repeat: "450px" }],
            }}
          />
          {shifted && (
            <Polyline
              key="actual-trace"
              path={shifted}
              options={{
                strokeColor: "#FF0000",
                strokeWeight: 4,
                strokeOpacity: 1,
                icons: [{ icon: traceSymbol, offset: "0%", repeat: "200px" }],
              }}
            />
          )}
          <TraceEndpoints
            key="trace-endpoints"
            path={shifted || scheduleRoute.shape}
          />
        </>
      )
    }

    // While the schedule fetch is in flight, draw nothing: flashing the raw
    // GPS trace (straight segments between sparse fixes) only to swap it for
    // the official road-following line a moment later reads as a glitch.
    if (isScheduleLoading) return null

    // A GTFS vehicle whose schedule answered without a line geometry keeps
    // the map clean rather than showing the misleading straight GPS trace.
    if (scheduleRoute?.schedule_available && !scheduleRoute?.shape?.length) return null

    if (!showRouteDetail) return null

    return directionsList?.map((dir, i) => {
      if (!dir) return null

      if (selectedRouteIndex === null) return null;

      if (selectedRouteIndex !== i) return null;

      const isSelected = i === selectedRouteIndex
      const color = vehicleRouteColors[i % vehicleRouteColors.length]

      const shifted = offsetPolylinePath(dir, i % 2 === 0 ? 2 : -2)

      const lineSymbol = {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 3,
        strokeColor: isSelected ? "#FF0000" : color,
      }

      return (
        <Fragment key={`route-${i}`}>
          <Polyline
            key={`poly-${i}`}
            path={shifted}
            options={{
              strokeColor: isSelected ? "#FF0000" : color,
              strokeWeight: 4,
              strokeOpacity: 1,
              icons: [{ icon: lineSymbol, offset: "0%", repeat: "450px" }],
            }}
          />
          <TraceEndpoints key={`poly-endpoints-${i}`} path={shifted} />
        </Fragment>
      )
    })
  }, [directionsList, selectedRouteIndex, showRouteDetail, scheduleRoute, isScheduleLoading])

  // ✅ MEMOIZED STOP MARKERS — the selected vehicle's stops along its route
  const stopMarkersMemo = useMemo(() => {
    if (!mapLoaded || !window.google?.maps) return null
    if (selectedRouteIndex === null) return null

    const showLabels = showStopLabels

    // GTFS-attached vehicles: the matched trip's own stops (with scheduled /
    // estimated times) — otherwise the GPS-derived chain. Schedule stops stay
    // visible at any zoom (the vehicle is explicitly selected); the GPS chain
    // keeps the zoom gate.
    const scheduleStops = (scheduleRoute?.stops || []).map((s) => ({
      id: `${s.gtfs_stop_id}-${s.sequence}`,
      lat: s.lat,
      lng: s.lng,
      address: s.name,
      stopType: s.status,
      scheduled: s.scheduled,
      estimated: s.estimated,
    }))

    if (!showRouteDetail && !scheduleStops.length) return null

    const past = (vehicleStops?.past || []).map((s) => ({ ...s, stopType: "past" }))
    const current = vehicleStops?.current
      ? [{ ...vehicleStops.current, stopType: "current" }]
      : []
    const future = (vehicleStops?.future || []).map((s) => ({ ...s, stopType: "future" }))

    // Blue family so the dots stand out against the red route polyline.
    // Passed stops render smaller and dimmer so a long chain doesn't clutter.
    const stopColors = { past: "#90a4ae", current: "#ff6d00", future: "#1a73e8" }
    const stopScale = { past: 4, current: 7, future: 5 }
    const stopOpacity = { past: 0.6, current: 1, future: 1 }

    const shortName = (name = "") => (name.length > 22 ? `${name.slice(0, 20)}…` : name)

    const source = scheduleStops.length ? scheduleStops : [...past, ...current, ...future]

    return source
      .filter((s) => s?.lat != null && s?.lng != null)
      .map((s, i) => (
        <Marker
          key={`stop-${s.id || i}`}
          position={{ lat: parseFloat(s.lat), lng: parseFloat(s.lng) }}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: stopScale[s.stopType],
            fillColor: stopColors[s.stopType],
            fillOpacity: stopOpacity[s.stopType],
            strokeColor: "#ffffff",
            strokeWeight: 2,
            // Place the name label just above the dot (path units × scale).
            labelOrigin: new window.google.maps.Point(0, -2.2),
          }}
          label={
            showLabels
              ? {
                  text: shortName(s.address),
                  color: "#0d47a1",
                  fontSize: "11px",
                  fontWeight: "700",
                }
              : undefined
          }
          zIndex={999}
          onClick={() =>
            setSelectedStopInfo({
              ...s,
              lat: parseFloat(s.lat),
              lng: parseFloat(s.lng),
            })
          }
        />
      ))
  }, [vehicleStops, scheduleRoute, selectedRouteIndex, mapLoaded, showRouteDetail, showStopLabels])

  // ✅ MEMOIZED MARKERS
  // const markersMemo = useMemo(() => {
  //   if (!locations?.length) return null
  //   return (
  //     <MarkerClusterer
  //       options={{
  //         styles: [
  //           {
  //             url: mapBus,
  //             height: 70,
  //             width: 60,
  //             textColor: "#ea050cff",
  //             textSize: 30,
  //             anchorText: [25, 35]
  //           },
  //         ],
  //         minimumClusterSize: 2,
  //         gridSize: 60,
  //       }}
  //     >
  //       {(clusterer) =>
  //         locations.map((loc, i) => {
  //           const poly = directionsList[i] || []
  //           const last = poly.length ? poly[poly.length - 1] : loc
  //           return (
  //             <Marker
  //               key={i}
  //               position={{ lat: last.lat, lng: last.lng }}
  //               icon={getMarkerIcon(loc.logoUrl)}
  //               clusterer={clusterer}
  //               onClick={() => {
  //                 setSelectedLocation(loc)
  //                 setSelectedRouteIndex(i)
  //               }}
  //             />
  //           )
  //         })
  //       }
  //     </MarkerClusterer>
  //   )
  // }, [locations, directionsList, getMarkerIcon])

  // Where each vehicle's marker actually sits on the map. Vehicles parked at
  // the same spot fully overlap: the clusterer shows a permanent "2" that no
  // zoom can break apart, so neither bus is ever clickable. Spread co-located
  // markers on a small circle (~15 m) so past clustering zoom each one is
  // visible and clickable on its own. Shared by the markers, the selection
  // highlight ring, and the search pan — all three must agree on the spot.
  const adjustedPositions = useMemo(() => {
    const isToday = moment(selectedDate).isSame(new Date(), 'day')
    const basePositions = locations.map((location, index) => {
      const polylinePath = directionsList[index] || []
      // Polylines load on demand now — past dates fall back to the route's
      // end point (included in the light payload) for the marker position.
      const lastPoint = polylinePath.length > 0
        ? polylinePath[polylinePath.length - 1]
        : location?.route_end || null
      return (!isToday && lastPoint)
        ? { lat: lastPoint.lat, lng: lastPoint.lng }
        : { lat: location.lat, lng: location.lng }
    })

    const cellOf = (p) => `${p.lat?.toFixed?.(4)}|${p.lng?.toFixed?.(4)}` // ~11 m cell
    const cellMembers = {}
    basePositions.forEach((p, i) => {
      const k = cellOf(p)
      if (!cellMembers[k]) cellMembers[k] = []
      cellMembers[k].push(i)
    })

    return basePositions.map((p, i) => {
      const group = cellMembers[cellOf(p)] || []
      if (group.length < 2 || !Number.isFinite(p.lat)) return p
      const j = group.indexOf(i)
      const angle = (2 * Math.PI * j) / group.length
      const r = 0.00013 // ~15 m
      return {
        lat: p.lat + r * Math.cos(angle),
        lng: p.lng + (r * Math.sin(angle)) / Math.cos((p.lat * Math.PI) / 180),
      }
    })
  }, [locations, directionsList, selectedDate])

  // Pulsing ring under the selected vehicle — without it, picking a bus from
  // the search dropdown never showed WHICH bus was meant (two can sit side by
  // side at a terminus). Follows the live position as it refreshes.
  const highlightMemo = useMemo(() => {
    if (activeVehicleIndex === null) return null
    const p = adjustedPositions[activeVehicleIndex]
    if (!p || !Number.isFinite(p.lat)) return null
    return (
      <OverlayView position={p} mapPaneName={OverlayView.OVERLAY_LAYER}>
        <div className="vehicle-highlight-ring" />
      </OverlayView>
    )
  }, [activeVehicleIndex, adjustedPositions])

  // Two-step flow: first click draws the route line and stops on the map; a
  // second click on the same bus opens the panel. The line survives both the
  // panel opening and closing — it only changes when another bus is picked.
  const handleVehicleClick = (location, index) => {
    // Full click cycle: 1st click shows the route, 2nd opens the panel,
    // closing the panel keeps the route, the NEXT click hides it, and the
    // one after starts the cycle fresh.
    if (closedPanelIndexRef.current === index) {
      closedPanelIndexRef.current = null
      activeVehicleIndexRef.current = null
      isPolylineVisibleRef.current = false
      setActiveVehicleIndex(null)
      setSelectedRouteIndex(null)
      setIsPolylineVisible(false)
      setScheduleRoute(null)
      setSelectedLocation(null)
      setSelectedStopInfo(null)
      // The dropdown pick goes with the focus — otherwise the search kept
      // naming a bus the map no longer singled out.
      setSelectedVehicle(null)
      return
    }
    closedPanelIndexRef.current = null

    const isRepeatClick =
      activeVehicleIndexRef.current === index && isPolylineVisibleRef.current
    // One physical click can fire twice: selecting the bus remounts its
    // marker (clustered → standalone) under the cursor and the second firing
    // then read as the "open panel" click. Only near-instant repeats are
    // synthetic — a real double-click (~150 ms+) must open the panel
    // immediately, with the skeleton if the data is still loading.
    if (isRepeatClick && Date.now() - (lastSelectRef.current?.at || 0) < 150) return
    if (isRepeatClick) {
      routeVehicleRef.current = location
      setRouteVehicle(location)
      setRouteModalOpen(true)
      // If the first click's fetch is still in flight the panel shows its
      // skeleton and that fetch fills it; otherwise refresh silently.
      if (!isStopsLoading && !isScheduleLoading) {
        fetchVehicleStops(location.vehicleId, { silent: true })
      }
      return
    }
    lastSelectRef.current = { index, at: Date.now() }
    activeVehicleIndexRef.current = index
    isPolylineVisibleRef.current = true
    setActiveVehicleIndex(index)
    setSelectedRouteIndex(index)
    setIsPolylineVisible(true)
    // Tooltip with the vehicle's identity (name + device serial) opens with
    // the route on the first click.
    setSelectedLocation({ ...location, index })
    fetchVehicleStops(location.vehicleId, { fitRoute: true })
  }

  const statusDot = (location, position) => {
    const baseSize = 40
    const scaleFactor = Math.pow(0.95, 12 - iconZoom)
    const markerSize = Math.round(baseSize * scaleFactor)
    const dotOffsetBottom = markerSize * 1.1
    const dotOffsetRight = markerSize * 0.45
    const minScale = Math.max(0.4, scaleFactor)

    return (
      <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: location?.is_online ? '#00ff00' : '#ff0000',
            borderRadius: '50%',
            border: '2px solid white',
            position: 'relative',
            bottom: `${dotOffsetBottom}px`,
            right: `-${dotOffsetRight}px`,
            transform: `scale(${minScale})`,
            zIndex: 1000,
          }}
        />
      </OverlayView>
    )
  }

  const markersMemo = useMemo(() => {
    const isToday = moment(selectedDate).isSame(new Date(), 'day')
    const selectedLoc = activeVehicleIndex !== null ? locations[activeVehicleIndex] : null

    return (
      <>
        {/* While a specific vehicle is selected (search or map click), show
            ONLY that vehicle: the other markers and their cluster badges are
            noise around the focused route. Clearing the selection brings
            everyone back. */}
        {activeVehicleIndex === null && (
        <MarkerClusterer
          options={{
            styles: [
              {
                url: mapBus,
                height: 70,
                width: 60,
                textColor: "#ea050cff",
                textSize: 30,
                anchorText: [25, 35],
              }
            ],
            // Past street-level zoom every bus stands alone — without this,
            // co-located vehicles stayed clustered forever.
            maxZoom: 17,
          }}
          averageCenter
          enableRetinaIcons
          gridSize={60}
        >
          {(clusterer) =>
            locations.map((location, index) => {
              // The selected vehicle renders OUTSIDE the clusterer (below):
              // clustered, its icon sat at the cluster centroid while the
              // highlight ring stayed on the true position — circle beside
              // the bus instead of under it.
              if (index === activeVehicleIndex) return null
              // "Online only" filter: hide offline vehicles' markers and dots.
              if (showOnlineOnly && !location?.is_online) return null
              const markerPosition = adjustedPositions[index]

              return (
                <Fragment key={`vehicle-${location.vehicleId || index}`}>
                  <Marker
                    key={index}
                    position={markerPosition}
                    icon={getMarkerIcon(location.logoUrl)}
                    clusterer={clusterer}
                    onClick={() => handleVehicleClick(location, index)}
                  />

                  {isToday && statusDot(location, markerPosition)}
                </Fragment>
              )
            })
          }
        </MarkerClusterer>
        )}

        {selectedLoc && adjustedPositions[activeVehicleIndex] && (
          <>
            <Marker
              key={`selected-${activeVehicleIndex}`}
              position={adjustedPositions[activeVehicleIndex]}
              icon={getMarkerIcon(selectedLoc.logoUrl)}
              zIndex={1001}
              onClick={() => handleVehicleClick(selectedLoc, activeVehicleIndex)}
            />
            {isToday && statusDot(selectedLoc, adjustedPositions[activeVehicleIndex])}
          </>
        )}
      </>
    )
  }, [locations, adjustedPositions, getMarkerIcon, iconZoom, selectedDate, activeVehicleIndex, showOnlineOnly])

  // const startPointsMemo = useMemo(() => {
  //   return startPoints.map((s, i) => {
  //     const isSelected = i === selectedRouteIndex
  //     return (
  //       <OverlayView
  //         key={i}
  //         position={s.position}
  //         mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
  //       >
  //         <div
  //           onClick={() =>
  //             setSelectedStartPoint({
  //               ...s,
  //               lat: s.position.lat,
  //               lng: s.position.lng,
  //             })
  //           }
  //           style={{
  //             width: "14px",
  //             height: "14px",
  //             backgroundColor: isSelected ? "#2175ff" : s.color,
  //             borderRadius: "50%",
  //             border: "2px solid white",
  //             boxShadow: "0 0 6px rgba(0,0,0,0.5)",
  //             cursor: "pointer",
  //           }}
  //         />
  //       </OverlayView>
  //     )
  //   })
  // }, [startPoints, selectedRouteIndex])

  return (
    <>
      <div className="map-date-picker-div">
        <div className="map-date-picker-inner-div">
          {user?.role === 'superAdmin' && (
            <Select
              className="map-org-select"
              mode="multiple"
              maxTagCount={1}
              showSearch
              allowClear
              placeholder={translations[language]?.organizationSelect || 'Select Organization'}
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              value={orgFilter}
              onChange={handleOrgChange}
            >
              {organizations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.organization_name}
                </Option>
              ))}
            </Select>
          )}

          {canFilterGtfs && gtfsFeeds.length > 0 && (
            <Select
              className="map-gtfs-select"
              mode="multiple"
              maxTagCount={1}
              showSearch
              allowClear
              placeholder={translations[language]?.gtfsFeeds || 'GTFS Feeds'}
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              value={gtfsFilter}
              onChange={handleGtfsChange}
            >
              {gtfsFeeds.map((feed) => (
                <Option key={feed.feed_source} value={feed.feed_source}>
                  {`${feed.feed_name || feed.feed_source}${user?.role === 'superAdmin' && orgNameById[feed.organization_id]
                      ? ` (${orgNameById[feed.organization_id]})`
                      : ''
                    }`}
                </Option>
              ))}
            </Select>
          )}

          <Select
            className="map-vehicle-select"
            showSearch
            allowClear
            placeholder={translations[language]?.selectVehicle}
            // Search must match what the user sees (the vehicle name), not the
            // option value — with the old JSON value, typing "916" matched any
            // vehicle whose coordinates or ids contained "916".
            filterOption={(input, option) =>
              String(option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(vehicleId) => {
              // Cleared via the ✕ (allowClear): drop the focused vehicle and
              // bring the whole fleet back.
              if (!vehicleId) {
                setSelectedVehicle(null)
                setSelectedLocation(null)
                setSelectedRouteIndex(null)
                setActiveVehicleIndex(null)
                setIsPolylineVisible(false)
                setScheduleRoute(null)
                setSelectedStopInfo(null)
                setRouteModalOpen(false)
                routeVehicleRef.current = null
                setRouteVehicle(null)
                activeVehicleIndexRef.current = null
                isPolylineVisibleRef.current = false
                closedPanelIndexRef.current = null
                return
              }
              const idx = vehicles.findIndex((x) => x.vehicleId === vehicleId)
              const v = vehicles[idx]
              if (!v) return
              setSelectedVehicle(vehicleId)
              setSelectedLocation(v)
              setSelectedRouteIndex(idx)
              setActiveVehicleIndex(idx)
              setIsPolylineVisible(true)
              // Deliberately NOT arming the click toggle
              // (activeVehicleIndexRef/isPolylineVisibleRef): the search only
              // shows the line — the first map click on the bus must stay a
              // "first click", not jump straight into the panel.
              activeVehicleIndexRef.current = null
              isPolylineVisibleRef.current = false
              closedPanelIndexRef.current = null
              fetchVehicleStops(v.vehicleId, { fitRoute: true })
              // Center on the marker's actual (spread-adjusted) spot so the
              // searched vehicle is found instantly; when its schedule
              // lands, fitRoute zooms the whole line into view — the same
              // auto-zoom a direct map click produces, so both flows look
              // identical. (A hard zoom-18 here fought the fit: it showed a
              // cropped street-level slice of the route first.)
              const pos = adjustedPositions[idx] || { lat: v.lat, lng: v.lng }
              mapRef.current?.panTo(pos)
            }}
            value={selectedVehicle}
          >
            {vehicles.map((v) => (
              <Option key={v.vehicleId} value={v.vehicleId}>
                {`${v.vehicleName}${v.ud_sn ? ` (${v.ud_sn})` : ''}`}
              </Option>
            ))}
          </Select>

          <div className='map-online-filter'>
            <button
              className={`map-filter-option ${!showOnlineOnly ? 'active' : ''}`}
              onClick={() => setShowOnlineOnly(false)}
            >
              {translations[language]?.all}
            </button>
            <button
              className={`map-filter-option ${showOnlineOnly ? 'active' : ''}`}
              onClick={() => setShowOnlineOnly(true)}
            >
              <span className='map-filter-dot' />
              {translations[language]?.online}
            </button>
          </div>

          <Datepicker
            format={dateFormate}
            value={selectedDate}
            language={language}
            onChange={(d) => setSelectedDate(d)}
          />
        </div>
      </div>

      <div style={{ position: 'relative', width: '100%', height: '100%' }}>

        {isMapScriptLoaded && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={getCurrentCenter()}
            zoom={12}
            onLoad={onMapLoad}
            onZoomChanged={onZoomChanged}
            options={{
              gestureHandling: "greedy",
              disableDefaultUI: true,
            }}
          >
            {markersMemo}
            {highlightMemo}
            {polylinesMemo}
            {stopMarkersMemo}
            {/* {startPointsMemo} */}

            {/* Vehicle identity tooltip — opens with the route on click. */}
            {selectedLocation && activeVehicleIndex !== null && adjustedPositions[activeVehicleIndex] && (
              <InfoWindow
                position={adjustedPositions[activeVehicleIndex]}
                options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
                onCloseClick={() => setSelectedLocation(null)}
              >
                <div>
                  <div>
                    <strong>{translations[language]?.vechicleName}:</strong>{" "}
                    {selectedLocation.vehicleName}
                  </div>
                  {selectedLocation.serialNumber && (
                    <div>
                      <strong>{translations[language]?.serialNumber}:</strong>{" "}
                      {selectedLocation.serialNumber}
                    </div>
                  )}
                  {selectedLocation.ud_sn && (
                    <div>
                      <strong>{translations[language]?.deviceId}:</strong>{" "}
                      {selectedLocation.ud_sn}
                    </div>
                  )}
                  <div>
                    <strong>{translations[language]?.txtRemainingPassengers}:</strong>{" "}
                    {selectedLocation.remaining || 0}
                  </div>
                  {Number.isFinite(selectedLocation.lat) && (
                    <div>
                      <strong>📍</strong> {selectedLocation.lat.toFixed(6)},{" "}
                      {selectedLocation.lng.toFixed(6)}
                    </div>
                  )}
                  {selectedLocation.report_time && (
                    <div>
                      <strong>🕒</strong> {formatReportTime(selectedLocation.report_time)}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}

            {selectedStopInfo && (
              <InfoWindow
                position={{
                  lat: selectedStopInfo.lat,
                  lng: selectedStopInfo.lng,
                }}
                onCloseClick={() => setSelectedStopInfo(null)}
              >
                <div>
                  <div>
                    <strong>{selectedStopInfo.address}</strong>
                  </div>
                  {selectedStopInfo.scheduled && (
                    <div>
                      🕒 {translations[language]?.scheduledShort}:{" "}
                      <strong>{selectedStopInfo.scheduled}</strong>
                      {selectedStopInfo.estimated &&
                        selectedStopInfo.estimated !== selectedStopInfo.scheduled && (
                          <>
                            {" · "}
                            {translations[language]?.estimatedShort}:{" "}
                            <strong>{selectedStopInfo.estimated}</strong>
                          </>
                        )}
                    </div>
                  )}
                  {!selectedStopInfo.scheduled && selectedStopInfo.stopType === "future" && (
                    <div>
                      🕒 {translations[language]?.reachIn}{" "}
                      <strong>{selectedStopInfo.eta_minutes || 0}</strong>{" "}
                      {translations[language]?.minutes}
                    </div>
                  )}
                  {!selectedStopInfo.scheduled && selectedStopInfo.stopType === "past" && (
                    <div>
                      🕒{" "}
                      {selectedStopInfo.stop_reach_time && (
                        <>
                          {translations[language]?.reachedAt}{" "}
                          <strong>
                            {moment(selectedStopInfo.stop_reach_time, "YYYY-MM-DD HH:mm:ss").format("h:mm A")}
                          </strong>
                          {" · "}
                        </>
                      )}
                      {translations[language]?.stoppedFor}{" "}
                      <strong>{selectedStopInfo.total_minutes ?? 0}</strong>{" "}
                      {translations[language]?.minutes}
                    </div>
                  )}
                  {selectedStopInfo.stopType === "current" && (
                    <div>{translations[language]?.currentBusLocation}</div>
                  )}
                </div>
              </InfoWindow>
            )}

            {selectedStartPoint && (
              <InfoWindow
                position={{
                  lat: selectedStartPoint.lat,
                  lng: selectedStartPoint.lng,
                }}
                onCloseClick={() => setSelectedStartPoint(null)}
              >
                <div>
                  {translations[language]?.journeyStartedMessage1}
                  <strong>{selectedStartPoint.vehicleName}</strong>
                  {translations[language]?.journeyStartedMessage2}

                </div>
              </InfoWindow>
            )}
            {/* {selectedLocation && (
              <InfoWindow
                position={{
                  lat: selectedLocation.lat,
                  lng: selectedLocation.lng,
                }}
                onCloseClick={() => {
                  setSelectedLocation(null)
                  setSelectedRouteIndex(null)
                }}
              >
                <div>
                  <div>
                    <strong>{translations[language]?.vechicleName}:</strong>{" "}
                    {selectedLocation.vehicleName}
                  </div>
                  <div>
                    <strong>{translations[language]?.vehicleModelLabel}:</strong>{" "}
                    {selectedLocation.vehicleModel}
                  </div>
                  <div>
                    <strong>{translations[language]?.txtRemainingPassengers}:</strong>{" "}
                    {selectedLocation.remaining || 0}
                  </div>
                  {isTodaySelected() ? (
                    <div>
                      {getDurationAgo(
                        selectedLocation.start_time,
                        null,
                        true
                      )}
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>Started at:</strong>{" "}
                        {formatStartTime(selectedLocation.start_time)}
                      </div>
                      <div>
                        <strong>Stopped at:</strong>{" "}
                        {formatStartTime(selectedLocation.end_time)}
                      </div>
                      <div>
                        {getDurationAgo(
                          selectedLocation.start_time,
                          selectedLocation.end_time,
                          false
                        )}
                      </div>
                    </>
                  )}
                </div>
              </InfoWindow>
            )} */}
          </GoogleMap>
        )}

          {isLoading && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '1.5em',
              color: '#333',
              backgroundColor: 'rgb(0 0 0 / 30%)',
            }}>
              <div className="spinner-border" role="status">
                <div class="dot-spinner">
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                </div>
                <span className="sr-only">{translations[language]?.loadingMap}</span>
              </div>
            </div>
          )}
      </div>
      <CustomModal
        isVisible={routeModalOpen}
        onClose={() => {
          // Closing the panel keeps the vehicle focused: its route, its lone
          // marker and the dropdown pick stay exactly as before the panel.
          // Dropping the whole selection here brought the entire fleet back,
          // and at a depot where several buses share one spot the bus under
          // the cursor was suddenly a different one (67 "became" 302). The
          // next click on the bus hides its route (the click cycle in
          // handleVehicleClick); the ✕ of the vehicle search clears the focus.
          setRouteModalOpen(false)
          routeVehicleRef.current = null
          setRouteVehicle(null)
          setSelectedStopInfo(null)
          closedPanelIndexRef.current = activeVehicleIndexRef.current ?? activeVehicleIndex
        }}
        title={translations[language]?.vehicleRouteTitle}
        language={language}
      >
        {routeVehicle ? (
          <VehicleRoute language={language} vehicleStops={vehicleStops} vehicle={routeVehicle} forecastDevice={routeVehicle?.ud_sn || routeVehicle?.serial_number} past={vehicleStops?.past} current={vehicleStops?.current} loading={isStopsLoading || isScheduleLoading} future={vehicleStops?.future} schedule={scheduleRoute} />
        ) : (
          <p>No route found</p>
        )}
      </CustomModal>

    </>
  )
}

export default Maps